import React, { useState, useEffect } from 'react';
import projectService from '../../services/projectService';
import './BaselineForm.css';

const BaselineForm = ({ baseline, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    description: '',
    baseline_type: 'Initial',
    version: '1.0',
    status: 'Draft',
    baseline_date: new Date().toISOString().split('T')[0],
    scope_description: '',
    deliverables: [],
    acceptance_criteria: '',
    planned_start_date: '',
    planned_end_date: '',
    planned_duration_days: '',
    milestones: [],
    planned_budget: '',
    planned_labor_cost: '',
    planned_material_cost: '',
    planned_equipment_cost: '',
    planned_other_cost: '',
    contingency_reserve: '',
    management_reserve: '',
    assumptions: '',
    constraints: '',
    notes: ''
  });

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deliverableInput, setDeliverableInput] = useState('');
  const [milestoneInput, setMilestoneInput] = useState({ name: '', date: '' });

  useEffect(() => {
    fetchProjects();
    if (baseline) {
      setFormData({
        ...baseline,
        baseline_date: baseline.baseline_date?.split('T')[0] || '',
        planned_start_date: baseline.planned_start_date?.split('T')[0] || '',
        planned_end_date: baseline.planned_end_date?.split('T')[0] || '',
        deliverables: baseline.deliverables || [],
        milestones: baseline.milestones || []
      });
    }
  }, [baseline]);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 1000 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-calculate duration if both dates are set
    if (name === 'planned_start_date' || name === 'planned_end_date') {
      const startDate = name === 'planned_start_date' ? value : formData.planned_start_date;
      const endDate = name === 'planned_end_date' ? value : formData.planned_end_date;

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setFormData(prev => ({
          ...prev,
          planned_duration_days: diffDays
        }));
      }
    }
  };

  const handleAddDeliverable = () => {
    if (deliverableInput.trim()) {
      setFormData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, deliverableInput.trim()]
      }));
      setDeliverableInput('');
    }
  };

  const handleRemoveDeliverable = (index) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }));
  };

  const handleAddMilestone = () => {
    if (milestoneInput.name.trim() && milestoneInput.date) {
      setFormData(prev => ({
        ...prev,
        milestones: [...prev.milestones, { ...milestoneInput }]
      }));
      setMilestoneInput({ name: '', date: '' });
    }
  };

  const handleRemoveMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="baseline-form">
      {/* Basic Information */}
      <div className="sections-row">
              <div className="form-section">
        <h3>Basic Information</h3>
        <div className="form-row custom-baseline-form">
          <div className="form-group">
            <label htmlFor="project_id">Project *</label>
            <select
              id="project_id"
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              required
              disabled={!!baseline}
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="name">Baseline Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Q1 2024 Baseline"
            />
          </div>
        </div>

        <div className="form-row custom-baseline-form">
          <div className="form-group">
            <label htmlFor="baseline_type">Baseline Type *</label>
            <select
              id="baseline_type"
              name="baseline_type"
              value={formData.baseline_type}
              onChange={handleChange}
              required
            >
              <option value="Initial">Initial</option>
              <option value="Revised">Revised</option>
              <option value="Current">Current</option>
              <option value="Final">Final</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="version">Version *</label>
            <input
              type="text"
              id="version"
              name="version"
              value={formData.version}
              onChange={handleChange}
              required
              placeholder="e.g., 1.0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="baseline_date">Baseline Date *</label>
            <input
              type="date"
              id="baseline_date"
              name="baseline_date"
              value={formData.baseline_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            placeholder="Brief description of this baseline"
          />
        </div>
      </div>

      {/* Scope Baseline */}
      <div className="form-section">
        <h3>Scope Baseline</h3>
        <div className="form-group">
          <label htmlFor="scope_description">Scope Description</label>
          <textarea
            id="scope_description"
            name="scope_description"
            value={formData.scope_description}
            onChange={handleChange}
            rows="4"
            placeholder="Describe the project scope"
          />
        </div>

        <div className="form-group">
          <label>Deliverables</label>
          <div className="list-input">
            <input
              type="text"
              value={deliverableInput}
              onChange={(e) => setDeliverableInput(e.target.value)}
              placeholder="Add deliverable"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDeliverable())}
            />
            <button type="button" onClick={handleAddDeliverable} className="btn btn-secondary">
              Add
            </button>
          </div>
          <ul className="item-list">
            {Array.isArray(formData.deliverables)
              ? formData.deliverables.map((item, index) => (
                <li key={index}>
                  {item}
                  <button type="button" onClick={() => handleRemoveDeliverable(index)} className="btn-remove">
                    ×
                  </button>
                </li>
              )) : null}
          </ul>
        </div>

        <div className="form-group">
          <label htmlFor="acceptance_criteria">Acceptance Criteria</label>
          <textarea
            id="acceptance_criteria"
            name="acceptance_criteria"
            value={formData.acceptance_criteria}
            onChange={handleChange}
            rows="3"
            placeholder="Define acceptance criteria"
          />
        </div>
      </div>
      </div>
 <div className="sections-row">
      {/* Schedule Baseline */}
      <div className="form-section">
        <h3>Schedule Baseline</h3>
        <div className="form-row custom-baseline-form">
          <div className="form-group">
            <label htmlFor="planned_start_date">Planned Start Date</label>
            <input
              type="date"
              id="planned_start_date"
              name="planned_start_date"
              value={formData.planned_start_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="planned_end_date">Planned End Date</label>
            <input
              type="date"
              id="planned_end_date"
              name="planned_end_date"
              value={formData.planned_end_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="planned_duration_days">Duration (Days)</label>
            <input
              type="number"
              id="planned_duration_days"
              name="planned_duration_days"
              value={formData.planned_duration_days}
              onChange={handleChange}
              min="0"
              placeholder="Auto-calculated"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Milestones</label>
          <div className="milestone-input">
            <input
              type="text"
              value={milestoneInput.name}
              onChange={(e) => setMilestoneInput(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Milestone name"
            />
            <input
              type="date"
              value={milestoneInput.date}
              onChange={(e) => setMilestoneInput(prev => ({ ...prev, date: e.target.value }))}
            />
            <button type="button" onClick={handleAddMilestone} className="btn btn-secondary">
              Add
            </button>
          </div>
          <ul className="item-list">
            {Array.isArray(formData.milestones)
              ? formData.milestones.map((item, index) => (
                <li key={index}>
                  <strong>{item.name}</strong> - {new Date(item.date).toLocaleDateString()}
                  <button type="button" onClick={() => handleRemoveMilestone(index)} className="btn-remove">
                    ×
                  </button>
                </li>
              )) : null}
          </ul>
        </div>
      </div>

      {/* Cost Baseline */}
      <div className="form-section">
        <h3>Cost Baseline</h3>
        <div className="form-row custom-baseline-form">
          <div className="form-group">
            <label htmlFor="planned_budget">Total Planned Budget</label>
            <input
              type="number"
              id="planned_budget"
              name="planned_budget"
              value={formData.planned_budget}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="planned_labor_cost">Labor Cost</label>
            <input
              type="number"
              id="planned_labor_cost"
              name="planned_labor_cost"
              value={formData.planned_labor_cost}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="planned_material_cost">Material Cost</label>
            <input
              type="number"
              id="planned_material_cost"
              name="planned_material_cost"
              value={formData.planned_material_cost}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="form-row custom-baseline-form">
          <div className="form-group">
            <label htmlFor="planned_equipment_cost">Equipment Cost</label>
            <input
              type="number"
              id="planned_equipment_cost"
              name="planned_equipment_cost"
              value={formData.planned_equipment_cost}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="planned_other_cost">Other Cost</label>
            <input
              type="number"
              id="planned_other_cost"
              name="planned_other_cost"
              value={formData.planned_other_cost}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="form-row custom-baseline-form">
          <div className="form-group">
            <label htmlFor="contingency_reserve">Contingency Reserve</label>
            <input
              type="number"
              id="contingency_reserve"
              name="contingency_reserve"
              value={formData.contingency_reserve}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="management_reserve">Management Reserve</label>
            <input
              type="number"
              id="management_reserve"
              name="management_reserve"
              value={formData.management_reserve}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>
      </div>
      </div>
<div className="sections-row">
      {/* Additional Information */}
      <div className="form-section">
        <h3>Additional Information</h3>
        <div className="form-group">
          <label htmlFor="assumptions">Assumptions</label>
          <textarea
            id="assumptions"
            name="assumptions"
            value={formData.assumptions}
            onChange={handleChange}
            rows="3"
            placeholder="List key assumptions"
          />
        </div>

        <div className="form-group">
          <label htmlFor="constraints">Constraints</label>
          <textarea
            id="constraints"
            name="constraints"
            value={formData.constraints}
            onChange={handleChange}
            rows="3"
            placeholder="List constraints"
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
            placeholder="Additional notes"
          />
        </div>
      </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : baseline ? 'Update Baseline' : 'Create Baseline'}
        </button>
      </div>
    </form>
  );
};

export default BaselineForm;


